import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {useExclusiveHover} from 'components/DSYS/useExclusiveHover';
import {useHoverShade} from 'components/DSYS/useHoverShade';
import type {ReactNode} from 'react';
import styles from 'views/workstreams/detail/WorkstreamDetailsShell.module.css';

import {logWorkstreamsInteraction} from './workstreamsAnalytics';

/**
 * A fold's button: chevron, then label, then an optional unbolded `detail`
 * (e.g. "7 open · 4 need attention"). Hover tints a pill around it (padding
 * pulled back by margin, so nothing shifts). The cards' section folds
 * (SectionFoldToggle) and the detail page's Members / Strategy / Activity
 * headers, which sit it beside controls of their own.
 */
export const SectionFoldButton = ({
  fold,
  label,
  detail,
  open,
  onToggle,
  ariaLabel,
}: {
  // Which fold this is, for analytics (labels can be translated).
  fold: string;
  label: ReactNode;
  detail?: ReactNode;
  open: boolean;
  onToggle: () => void;
  ariaLabel?: string;
}) => {
  const {hovered, onMouseEnter, onMouseLeave} = useExclusiveHover();
  const shade = useHoverShade(hovered);
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={ariaLabel}
      // Keeps an enclosing scan card from taking the hover (WorkstreamScanList).
      data-hovers-self
      onClick={(event) => {
        event.stopPropagation();
        logWorkstreamsInteraction('fold_toggled', {fold, expanded: !open});
        onToggle();
      }}
      // Mouse clicks don't take focus, so the focus ring only shows for keyboard use.
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // The detail page's toggle (styles.activityToggle) minus its transparent
      // background, which would fight the hover tint.
      className={`${atoms({
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: 'Small',
      })} ${shade.className}`}
      style={{
        ...shade.style,
        gap: 4,
        border: 0,
        padding: '2px 6px',
        margin: '-2px -6px',
        background: 'none',
      }}
    >
      <UIIcon
        src={open ? ChevronDownLine : ChevronRightLine}
        size="small"
        className={styles.activityToggleIcon}
      />
      <Text size="small" isBold color={hovered ? 'standard' : 'subtle'}>
        {label}
      </Text>
      {detail && (
        <Text size="small" color="subtle" style={{marginLeft: 4}}>
          {detail}
        </Text>
      )}
    </button>
  );
};

// A section label that folds its body, like the detail page's Members /
// Strategy / Activity rows. Closed, it drops the gap above the (absent) body.
export const SectionFoldToggle = ({
  open,
  ...button
}: {
  fold: string;
  label: string;
  detail?: ReactNode;
  open: boolean;
  onToggle: () => void;
}) => (
  <div
    className={
      open ? styles.activityHeader : `${styles.activityHeader} ${styles.activityHeaderCollapsed}`
    }
  >
    <SectionFoldButton open={open} {...button} />
  </div>
);
